/**
 * @fileoverview PKCE (Proof Key for Code Exchange) utility functions.
 *
 * @description
 * ELI5: PKCE is like a secret handshake for apps. When an app wants to log you
 * in, it creates a secret random string (the "Verifier") and a transformed
 * version of it (the "Challenge"). It sends the Challenge to the server first.
 * Later, it sends the secret Verifier to prove it's the same app that started
 * the login.
 *
 * ## What is PKCE?
 *
 * PKCE (Proof Key for Code Exchange, pronounced "pixy") is a security extension
 * to the OAuth 2.0 Authorization Code flow. Originally designed for mobile apps
 * (Public Clients), it is now the industry-standard recommendation for all
 * application types, including Single Page Applications (SPAs) and server-side
 * applications.
 *
 * ### The Problem It Solves
 *
 * In a standard flow, the "Authorization Code" is sent via the front channel
 * (the browser).  If an attacker intercepts this code—via a malicious app on a
 * device or by monitoring browser history—they could exchange it for an Access
 * Token. Since public clients cannot securely store a "Client Secret," there is
 * no way for the server to know if the entity exchanging the code is the same
 * one that requested it.
 *
 * ### Why we use it
 *
 * In Single Page Applications or Mobile Apps (Public Clients), we cannot safely
 * hide a Client Secret. Without a secret, a malicious actor could intercept the
 * Authorization Code during the redirect and exchange it for tokens.
 * PKCE fixes this by binding the authorization request to the token request
 * using a temporary, dynamic secret (the Verifier).
 *
 * ### The Solution (The PKCE Mechanism)
 *
 * PKCE ensures identity continuity via a dynamically created secret:
 *
 * 1. **Code Verifier**: The client generates a random, high-entropy string.
 * 2. **Code Challenge**: The client transforms the verifier (via SHA-256) and
 *    sends this challenge to the server during the initial request.
 * 3. **Verification**: When the client exchanges the code for a token, it sends
 *    the original unhashed `code_verifier`. The server hashes it; if it matches
 *    the original challenge, the token is issued.
 *
 * ## General flow:
 *
 * ```text
 *   App                                Auth Server
 *    |                                     |
 *    |--(1) Send Challenge --------------->| (Server saves challenge)
 *    |                                     |
 *    |<-(2) Return Auth Code --------------|
 *    |                                     |
 *    |--(3) Send Code + Verifier --------->| (Server hashes verifier and
 *    |                                     |  checks against saved challenge)
 *    |<-(4) Return Tokens -----------------|
 * ```
 *
 * ## Sequence Diagram (including failure paths)
 *
 * * ```text
 * +-------------+            +----------+           +---------------------+
 * |  Client     |            |  User    |           | Authorization Server |
 * | Application |            | Agent    |           |    (/authorize &    |
 * | (SPA/Mobile)|            | (Browser)|           |      /token)        |
 * +-------------+            +----------+           +---------------------+
 * |                          |                          |
 * Step 1    | 1. Generate locally:     |                          |
 * Generate  |    - code_verifier       |                          |
 * |    - code_challenge      |                          |
 * |    - Store verifier      |                          |
 * |                          |                          |
 * Step 2    | 2. Redirect to /authorize |                          |
 * AuthZ     |   ?code_challenge=xyz    |------------------------>|
 * Request   |   &method=S256           |                          |
 * |                          |                          |
 * <=========================================================================>
 * <|||                  AUTHORIZATION REQUEST FAILURES                   |||>
 * <=========================================================================>
 * |                          | (A) AS rejects if:       |
 * Failure A |                          |     - Missing PKCE       |
 * AS Rejects| <------------------------|     - Unknown method     |
 * |  HTTP 400 Bad Request    |     - Malformed challenge|
 * |                          |                          |
 * <=========================================================================>
 * <|||                          HAPPY PATH                               |||>
 * <=========================================================================>
 * |                          | (B) AS stores challenge   |
 * Step 4    |                          | 4. User signs in,        |
 * Login     |                          |<------------------------>|
 * Step 5    | 6. Redirect to client    | 5. Generate Auth Code    |
 * Redirect  |    with auth_code=456    | <------------------------|
 * |<-------------------------|                          |
 * |                          |                          |
 * Step 7    | 7. Exchange Code for Token|                          |
 * Token     |   (POST /token)          |                          |
 * Request   |   - auth_code=456        |------------------------>|
 * |   - code_verifier=101    |                          |
 * |                          |                          |
 * |                          | 8. PKCE Verification:    |
 * |                          |    - Hash(verifier)      |
 * |                          |    - Compare to stored   |
 * |                          |                          |
 * <=========================================================================>
 * <|||                      TOKEN EXCHANGE FAILURES                       |||>
 * <=========================================================================>
 * |                          | (C) Verification Fails:  |
 * Failure C | <------------------------+     - Hash mismatch !!!  |
 * Mismatch  | 9C. HTTP 400             |                          |
 * |    (invalid_grant)       |                          |
 * |                          |                          |
 * <=========================================================================>
 * <|||                     FINAL HAPPY PATH STEP                         |||>
 * <=========================================================================>
 * Success D | <------------------------+ 9D. Returns tokens       |
 * |                          |                          |
 * ```
 *
 * ## Common Pitfalls to Avoid
 *
 * 1. **Using `method: 'plain'`**: This offers almost no protection. Always use
 *    `S256`.
 *
 * 2. **Low Entropy Verifiers**: Use a cryptographically secure random number
 *    generator (CSPRNG). If the verifier is predictable, the challenge can be
 *    brute-forced.
 *
 * 3. **Insecure Storage**: The `code_verifier` should be kept in transient
 *    memory or sessionStorage`. Avoid `localStorage` if possible to limit
 *    persistence.
 *
 * 4. **Reusing Verifiers**: Generate a fresh `code_verifier` for every single
 *    request.
 *
 * 5. **Omitting `state`**: PKCE prevents code interception, but the `state`
 *    parameter is still necessary to protect against CSRF attacks.
 *
 * * @see {@link https://datatracker.ietf.org/doc/html/rfc7636 | RFC 7636}
 */

import {base64url} from '../codec';

/** Minimum length for the code verifier as per RFC 7636. */
const MIN_VERIFIER_LENGTH = 43;

/** Maximum length for the code verifier as per RFC 7636. */
const MAX_VERIFIER_LENGTH = 128;

/** Default length for the code verifier if not specified. */
const DEFAULT_VERIFIER_LENGTH = 43;

/** Maximum number of iterations for the random generation loop to prevent infinite loops. */
const MAX_ITERATIONS = 1000;

/** Set of unreserved characters allowed in the code verifier as per RFC 7636. */
const VERIFIER_CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/**
 * Generates a random code verifier string.
 *
 * @description
 * Complies with RFC 7636 Section 4.1:
 * "code_verifier = high-entropy cryptographic random string using the unreserved characters
 * [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~" from Section 2.3 of [RFC3986],
 * with a minimum length of 43 characters and a maximum length of 128 characters."
 *
 * @param length The length of the verifier (must be between 43 and 128).
 * @returns The generated code verifier.
 * @throws {Error} If length is not between 43 and 128.
 * @see https://datatracker.ietf.org/doc/html/rfc7636#section-4.1
 */
export function generateVerifier(
  length: number = DEFAULT_VERIFIER_LENGTH,
  getRandomValues = (array: Uint8Array): Uint8Array =>
    globalThis.crypto.getRandomValues(array),
): string {
  if (length < MIN_VERIFIER_LENGTH || length > MAX_VERIFIER_LENGTH) {
    throw new Error(
      `Length must be between ${MIN_VERIFIER_LENGTH} and ${MAX_VERIFIER_LENGTH}`,
    );
  }
  let text = '';
  const buffer = new Uint8Array(length * 2);
  getRandomValues(buffer);
  let offset = 0;
  for (
    let iterations = 0;
    iterations < MAX_ITERATIONS && text.length < length;
    iterations++
  ) {
    if (offset >= buffer.length) {
      getRandomValues(buffer);
      offset = 0;
    }
    const val = buffer[offset++];
    // Avoid modulo bias by rejecting values that fall into the incomplete bucket
    const maxValid = 256 - (256 % VERIFIER_CHARSET.length);
    if (val < maxValid) {
      text += VERIFIER_CHARSET.charAt(val % VERIFIER_CHARSET.length);
    }
  }

  if (text.length < length) {
    throw new Error('Failed to generate verifier: loop limit reached');
  }
  return text;
}

/**
 * Generates a code challenge from a code verifier using SHA-256.
 *
 * @description
 * Complies with RFC 7636 Section 4.2 (S256 method):
 * "code_challenge = BASE64URL-ENCODE(SHA256(ASCII(code_verifier)))"
 *
 * @param verifier The code verifier.
 * @returns A promise that resolves to the base64url encoded code challenge.
 * @see https://datatracker.ietf.org/doc/html/rfc7636#section-4.2
 */
export async function generateChallenge(verifier: string): Promise<string> {
  if (!globalThis.crypto.subtle) {
    throw new Error(
      'Crypto subtle API is not available. This might be due to non-secure context (HTTP).',
    );
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await globalThis.crypto.subtle.digest('SHA-256', data);
  return base64url(hash);
}

/**
 * Generates a PKCE challenge containing both code verifier and code challenge.
 *
 * @param length The length of the code verifier.
 * @returns A promise that resolves to an object containing code_verifier and code_challenge.
 */
export async function pkceChallenge(
  length: number = DEFAULT_VERIFIER_LENGTH,
  getRandomValues?: (array: Uint8Array) => Uint8Array,
): Promise<{code_verifier: string; code_challenge: string}> {
  const code_verifier = generateVerifier(length, getRandomValues);
  const code_challenge = await generateChallenge(code_verifier);
  return {code_verifier, code_challenge};
}
